import { GTMData } from "@helper/gtmData";
import { useNewRelic } from "@hooks/useNewRelic";
import { USFOrgSchema } from "@sphtech/web2-seo-schema";
import React, { useEffect } from "react";
import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useMatches,
} from "react-router";

type OauthLayoutProps = {
  children: React.ReactNode;
};

export function BaseLayout({ children }: { children: React.ReactNode }) {
  const matches = useMatches();
  useNewRelic();

  const gtm = matches.map((match) => {
    const loaderGtm =
      match.loaderData &&
      typeof match.loaderData === "object" &&
      "gtm" in match.loaderData
        ? (match.loaderData.gtm as GTMData)
        : undefined;
    return loaderGtm;
  });

  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />

        {/* Inject GTM data server-side */}
        <script
          suppressHydrationWarning // Add this to prevent hydration warnings from cookie logic
          dangerouslySetInnerHTML={{
            __html: `
            // Initialize GTM data layer from route handles
            var gtmdataLayer = ${JSON.stringify(gtm.filter(Boolean)[0] || {})};

            // Set defaults
            gtmdataLayer.visitorcat = gtmdataLayer.visitorcat || 1;
            gtmdataLayer.at = gtmdataLayer.at || "";
            gtmdataLayer.svc = gtmdataLayer.svc || "";
            gtmdataLayer.contentcat = gtmdataLayer.contentcat || 1;
            gtmdataLayer.cd_version = gtmdataLayer.cd_version || "tm2";
            gtmdataLayer.pagination = gtmdataLayer.pagination || 1;

            // Initialize data layer
            window.dataLayer = window.dataLayer || [];

            // Update with cookies if available (will run client-side)
            if (typeof window !== 'undefined' && typeof window.getCookieByName === 'function') {
              var visitorcat = window.getCookieByName("visitorcat");
              var mysphw = window.getCookieByName("mysphw");
              var svc = window.getCookieByName("svc");

              if (visitorcat != undefined) {
                gtmdataLayer.visitorcat = visitorcat;
              }
              if (mysphw != undefined) {
                gtmdataLayer.at = mysphw;
              }
              if (svc != undefined) {
                gtmdataLayer.svc = svc;
              }
            }

            // Set global references
            window.neuron_dataLayer = gtmdataLayer;
            window._data = gtmdataLayer;
            window.dataLayer.push(gtmdataLayer);`,
          }}
        />
        <Meta />
        <Links />
        <USFOrgSchema
          name="TamilMurasu"
          description="Get Latest Singapore, Tamil Nadu, India, World & Sports News in Tamil. சிங்கப்பூரின் ஒரே தமிழ் மொழி செய்தித்தாள். சிங்கப்பூர், இந்தியா, தமிழ்நாடு & விளையாட்டு செய்திகள்."
          url="https://www.tamilmurasu.com.sg/"
          logo={{
            url: "https://www.tamilmurasu.com.sg/web2-assets/tm_logo.webp",
            width: 630,
            height: 144,
          }}
          sameAs={[
            "https://whatsapp.com/channel/0029VadVjPV8fewnqkhfPd2X",
            "https://www.facebook.com/tamilmurasu/",
            "https://www.twitter.com/tamilmurasu",
            "https://www.instagram.com/tamil_murasu/",
            "https://www.youtube.com/@Tamil_Murasu",
          ]}
          contactPoint={{
            telephone: "+65 98633894",
            contactType: "Customer Service",
            availableLanguage: "English",
            areaServed: {
              name: "Singapore",
            },
          }}
        />
        <LightboxScript />
      </head>
      <body>
        {children}
        <div id="mySPHLightbox" suppressHydrationWarning />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function OauthLayout({ children }: OauthLayoutProps) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <Meta />
        <Links />
        <LightboxScript />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function LightboxScript(): React.ReactElement {
  // Coerce raw env values to string (or undefined) to avoid assigning `any` to JSX props
  const lightboxSrc = import.meta.env.VITE_MYSPH_LIGHTBOX2_URL
    ? String(import.meta.env.VITE_MYSPH_LIGHTBOX2_URL)
    : undefined;
  const lightboxClientId = import.meta.env.VITE_MYSPH_LIGHTBOX2_CLIENT_ID
    ? String(import.meta.env.VITE_MYSPH_LIGHTBOX2_CLIENT_ID)
    : undefined;

  useEffect(() => {
    if (lightboxSrc) {
      const script = document.createElement("script");
      script.src = lightboxSrc;
      script.type = "module";
      script.async = true;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [lightboxSrc]);
  return (
    <>
      <script
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: `
              window.mySPHConfig = {
                clientId: "${lightboxClientId}",
                pubId: "tm",
                language: "ta",
                providers: ["google", "facebook", "googleOneTap", "apple"],
                ssoMode: "manual",
              };

              if (!window.mySPHConfig.clientId || window.mySPHConfig.clientId === "undefined") {
                console.error("VITE_MYSPH_LIGHTBOX2_CLIENT_ID is not defined. Please check your environment variables.");
              }
            `,
        }}
      />
    </>
  );
}
